<template>
  <div class="auth flex flex-col items-center rounded-[32px] max-md:!px-8">
    <LoginTelegramAuthorizeForm
      v-if="mode === LoginMode.AUTH_TELEGRAM"
      :context="context"
      @set-mode="setMode"
      @authorized="$emit('authorized', $event)"
      class="w-full"
    />
    <LoginStepsEmailChoice
      v-else-if="mode === LoginMode.EMAIL_CHOICE"
      @set-mode="setMode"
    />
    <LoginStepsEmailPassword
      v-else-if="mode === LoginMode.EMAIL_ENTER || mode === LoginMode.EMAIL_PASSWORD"
      :email="email"
      :context="context"
      @set-mode="setMode"
      @set-email="email = $event"
      @set-registration-id="registrationId = $event"
      @set-expired-at="expiredAt = $event"
      @set-password="regPassword = $event"
      @authorized="$emit('authorized', $event)"
    />
    <LoginStepsRegisterEmail
      v-else-if="mode === LoginMode.REGISTER_EMAIL"
      :email="email"
      :context="context"
      @set-mode="setMode"
      @set-email="email = $event"
      @set-registration-id="registrationId = $event"
      @set-expired-at="expiredAt = $event"
      @set-password="regPassword = $event"
      @authorized="$emit('authorized', $event)"
    />
    <LoginStepsRegisterPassword
      v-else-if="mode === LoginMode.REGISTER_PASSWORD"
      :email="email"
      :registration-id="registrationId"
      @set-mode="setMode"
      @set-expired-at="expiredAt = $event"
      @set-password="regPassword = $event"
    />
    <LoginStepsConfirmCode
      v-else-if="mode === LoginMode.CONFIRM_CODE"
      :email="email"
      :registration-id="registrationId"
      :expired-at="expiredAt"
      :password="regPassword"
      @set-mode="setMode"
    />
    <LoginStepsCheckEmail
      v-else-if="mode === LoginMode.CHECK_EMAIL"
      :email="email"
      :registration-id="registrationId"
      @set-mode="setMode"
    />
    <LoginStepsRecoveryEmail
      v-else-if="mode === LoginMode.RECOVERY_EMAIL"
      :email="email"
      @set-mode="setMode"
      @set-email="email = $event"
    />
    <LoginStepsRecoverySent
      v-else-if="mode === LoginMode.RECOVERY_SENT"
      :email="email"
      @set-mode="setMode"
    />
    <LoginStepsBindMessenger
      v-else-if="mode === LoginMode.BIND_TELEGRAM"
      @bound="$emit('authorized', $event)"
      @close="$emit('authorized', $event)"
    />
  </div>
</template>

<script setup lang="ts">
  import useSetState from "~/composables/UI/useSetState";
  import { LoginMode, LoginScreenContext } from "~/interfaces/login/UI";
  import { useUserStore } from "~/store/UserModule";
  /* eslint-disable no-undef */
  interface Props {
    context?: LoginScreenContext;
    confirmedEmail?: boolean | undefined;
  }

  const props = withDefaults(defineProps<Props>(), {
    context: LoginScreenContext.null,
    confirmedEmail: undefined,
  });

  defineEmits(["authorized"]);

  const initialMode = props.confirmedEmail ? LoginMode.EMAIL_ENTER : LoginMode.AUTH_TELEGRAM;
  const [mode, _setMode] = useSetState(initialMode);

  const setMode = (newMode: LoginMode) => {
    // Останавливаем поллинг ТГ-авторизации при переключении на другой режим
    if (mode.value === LoginMode.AUTH_TELEGRAM && newMode !== LoginMode.AUTH_TELEGRAM) {
      userModule.stopTelegramAuthorize();
    }
    _setMode(newMode);
  };

  const email = ref('');
  const registrationId = ref(0);
  const expiredAt = ref('');
  const regPassword = ref('');

  const userModule = useUserStore();

  watch(() => userModule.loginDialogVisible, (visible) => {
    if (!visible) {
      userModule.stopTelegramAuthorize();
      _setMode(initialMode);
      email.value = '';
      registrationId.value = 0;
      expiredAt.value = '';
      regPassword.value = '';
    }
  });

  onMounted(() => {
    userModule.requestAuthKey();
  });
</script>
<style lang="scss">
  .auth {
    transition: none;
    width: 100% !important;
    align-self: center;
    padding: 32px 16px;
    margin: 0 !important;

    &,
    .col-gap-3 {
      justify-content: center;
      align-items: center;

      label {
        align-self: baseline;
      }
    }

    .col-gap-3 .col-gap-3 {
      max-width: 370px;
    }

    h1 {
      font-size: 28px;
      font-style: normal;
      font-weight: 700;
    }

    .auth-form {
      input {
        width: 100%;
      }

      .button-secondary {
        width: 100%;
      }
    }

    :deep(.el-dialog) {
      .el-dialog__body {
        width: 100%;
      }
    }

    @media (max-width: 578px) {
      span {
        width: fit-content;
      }
    }
  }

  .authorize-modal {
    .el-dialog {
      --el-dialog-border-radius: 16px;

      .el-dialog__header {
        padding-right: 0;

        &.show-close {
          padding-right: 0;
        }
      }

      .el-dialog__body {
        width: 100%;
      }
    }

    .screen-wrapper {
      max-width: fit-content;
      border: none;
      padding: 1rem;
      margin: auto;

      @media (max-width: 578px) {
        margin: 0;
        width: 100%;
        max-width: 100%;
      }
    }
  }
</style>
<style lang="scss" scoped>
.auth :deep(.loader-inline) {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  vertical-align: middle;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
